import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, RotateCcw, Database, ExternalLink } from "lucide-react";

const STATUS_BADGES = {
  synced: { label: "Synced", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  error: { label: "Fehler", color: "bg-red-100 text-red-800", icon: AlertCircle },
  stub_pending_sync: { label: "Stub (offline)", color: "bg-amber-100 text-amber-800", icon: AlertCircle },
  pending: { label: "Wartend", color: "bg-slate-100 text-slate-700", icon: AlertCircle },
};

export default function AdminAbaNinja() {
  const [data, setData] = useState({ customers: [], offers: [], invoices: [] });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resyncing, setResyncing] = useState(null);

  const load = () => Promise.all([
    api.get("/admin/abaninja/all"),
    api.get("/abaninja/status"),
  ]).then(([a, s]) => { setData(a.data); setStatus(s.data); setLoading(false); });

  useEffect(() => { load(); }, []);

  const resync = async (kind, id) => {
    setResyncing(`${kind}-${id}`);
    try {
      await api.post(`/admin/abaninja/${kind}/${id}/resync`);
      toast.success("Resync ausgelöst");
      load();
    } catch (e) {
      toast.error("Resync fehlgeschlagen");
    } finally { setResyncing(null); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-8" data-testid="admin-abaninja">
      <h1 className="font-heading text-3xl font-bold text-[#1A2433] mb-2">AbaNinja</h1>
      <p className="text-slate-600 mb-6">Synchronisierung zwischen Website und Buchhaltung.</p>

      {status && (
        <div className={`flex items-start gap-3 p-4 rounded-xl mb-8 ${
          status.mode === "live" ? "bg-emerald-50 border border-emerald-200" : "bg-amber-50 border border-amber-200"
        }`}>
          {status.mode === "live"
            ? <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
            : <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />}
          <div className="text-sm flex-1">
            <div className="font-semibold text-[#1A2433]">
              Status: {status.mode === "live" ? "Live verbunden" : "Stub-Modus"}
            </div>
            {status.account_uuid && (
              <div className="text-slate-600 mt-1 font-mono text-xs">Account: {status.account_uuid}</div>
            )}
            <div className="text-slate-600 mt-1">{status.note}</div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <Stat label="Kunden synchronisiert" value={data.customers.filter(c => c.status === "synced").length} total={data.customers.length} />
        <Stat label="Offerten synchronisiert" value={data.offers.filter(o => o.status === "synced").length} total={data.offers.length} />
        <Stat label="Rechnungen synchronisiert" value={data.invoices.filter(i => i.status === "synced").length} total={data.invoices.length} />
      </div>

      <Section title="Kunden" rows={data.customers} kind="customers" render={(c) => (
        <>
          <div className="font-medium text-[#1A2433]">{c.payload?.name || c.payload?.email}</div>
          <div className="text-xs text-slate-500">
            {c.payload?.email}
            {c.external_customer_number && <span> · Nr. <strong>{c.external_customer_number}</strong></span>}
            {c.external_id && <span className="font-mono text-[10px] ml-2">{c.external_id.slice(0, 8)}…</span>}
          </div>
          {c.error && <div className="text-xs text-red-600 mt-1 line-clamp-2">{c.error}</div>}
        </>
      )} onResync={resync} resyncing={resyncing} loading={loading} />

      <Section title="Offerten" rows={data.offers} kind="offers" render={(o) => (
        <>
          <div className="font-medium text-[#1A2433]">{o.payload?.service || "Offerte"}</div>
          <div className="text-xs text-slate-500">
            CHF {(o.payload?.total_chf || 0).toLocaleString("de-CH")}
            {o.raw?.quoteNumber && <span> · #{o.raw.quoteNumber}</span>}
            {o.external_id && <span className="font-mono text-[10px] ml-2">{o.external_id.slice(0, 8)}…</span>}
          </div>
          {o.error && <div className="text-xs text-red-600 mt-1 line-clamp-2">{o.error}</div>}
        </>
      )} onResync={resync} resyncing={resyncing} loading={loading} />

      <Section title="Rechnungen" rows={data.invoices} kind="invoices" render={(i) => (
        <>
          <div className="font-medium text-[#1A2433]">{i.payload?.service || "Rechnung"}</div>
          <div className="text-xs text-slate-500">CHF {(i.payload?.total_chf || 0).toLocaleString("de-CH")}</div>
          {i.error && <div className="text-xs text-red-600 mt-1 line-clamp-2">{i.error}</div>}
        </>
      )} onResync={resync} resyncing={resyncing} loading={loading} />
    </div>
  );
}

function Stat({ label, value, total }) {
  return (
    <div className="border border-slate-200 rounded-xl p-5">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-heading text-3xl font-bold text-[#1A2433] mt-1 tabular-nums">
        {value}<span className="text-base font-medium text-slate-400">/{total}</span>
      </div>
    </div>
  );
}

function Section({ title, rows, kind, render, onResync, resyncing, loading }) {
  return (
    <div className="mt-6">
      <h2 className="font-heading text-lg font-bold text-[#1A2433] mb-3">{title}</h2>
      {loading ? <div className="text-slate-500 text-sm">Lade…</div> :
        rows.length === 0 ? <div className="text-slate-500 text-sm bg-slate-50 rounded-lg p-4">Noch keine Daten.</div> :
        <div className="space-y-2">
          {rows.slice(0, 15).map((r) => {
            const badge = STATUS_BADGES[r.status] || STATUS_BADGES.pending;
            const BIcon = badge.icon;
            return (
              <div key={r.id} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg" data-testid={`ab-${kind}-${r.id}`}>
                <div className="flex-1 min-w-0">{render(r)}</div>
                <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${badge.color}`}>
                  <BIcon className="w-3 h-3" /> {badge.label}
                </span>
                {(r.status === "error" || r.status === "stub_pending_sync") && (
                  <button
                    onClick={() => onResync(kind, r.id)}
                    disabled={resyncing === `${kind}-${r.id}`}
                    className="text-[#991B1B] hover:text-[#7F1D1D] text-xs font-medium flex items-center gap-1 disabled:opacity-50"
                    data-testid={`resync-${kind}-${r.id}`}
                  >
                    <RotateCcw className="w-3 h-3" /> {resyncing === `${kind}-${r.id}` ? "…" : "Resync"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      }
    </div>
  );
}
